import React, { useEffect } from "react";
import { Button, Col, Modal, Row, Stack, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaFilter } from "react-icons/fa";
import {
  format,
  parse,
  differenceInMonths,
  isAfter,
  isValid,
  subMonths,
} from "date-fns";
import { useForm, Controller } from "react-hook-form";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { toast } from "react-toastify";

import { DateTimerPicker } from "../DateTimerPicker";
import { ReportsService } from "../../core/services/ReportsService";
import { VehicleService } from "../../core/services/VehicleService";

import { SensorsService } from "../../core/services/SensorsService";
import { useAppDispatch } from "../../core/redux/hooks";
import { updateAlert } from "../../core/redux/reducer/alert";
import { useNavigate } from "react-router-dom";

interface SensorsReportsModalProps {
  visible: boolean;
  onClose?: () => void;
  onChange?: (filters: {
    startDate?: Date;
    endDate?: Date;
    alertTypeId?: string | any;
    vehiclePlate?: string;
  }) => void;
}

interface FormValues {
  startDate: string;
  endDate: string;
  alertTypeId?: { value: string | Number; label: string };
  vehiclePlate?: VehicleOption | null;
}

interface VehicleOption {
  value: string;
  label: string;
}

const SensorsReportsModal: React.FC<SensorsReportsModalProps> = ({
  visible,
  onClose = () => {},
  onChange = () => {},
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { control, handleSubmit, watch, formState, setValue, getValues } =
    useForm<FormValues>({
      defaultValues: {
        startDate: format(subMonths(new Date(), 1), "dd/MM/yyyy HH:mm"),
        endDate: format(new Date(), "dd/MM/yyyy HH:mm"),
        alertTypeId: undefined,
        vehiclePlate: null,
      },
    });

  const { errors, isSubmitting } = formState;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const vehiclePlate = watch("vehiclePlate");

  useEffect(() => {
    if (onChange) {
      const parsedStartDate = parse(startDate, "dd/MM/yyyy HH:mm", new Date());
      const parsedEndDate = parse(endDate, "dd/MM/yyyy HH:mm", new Date());

      onChange({
        startDate: isValid(parsedStartDate) ? parsedStartDate : undefined,
        endDate: isValid(parsedEndDate) ? parsedEndDate : undefined,
        alertTypeId: getValues("alertTypeId"),
        vehiclePlate: vehiclePlate?.value,
      });
    }
  }, [startDate, endDate, vehiclePlate, onChange]);

  const validateDateRange = (start: string, end: string): string | null => {
    const startDateParsed = parse(start, "dd/MM/yyyy HH:mm", new Date());
    const endDateParsed = parse(end, "dd/MM/yyyy HH:mm", new Date());

    if (!isValid(startDateParsed) || !isValid(endDateParsed)) {
      return t("sensors_generatexls_modal.error.invalid_date");
    }
    if (differenceInMonths(endDateParsed, startDateParsed) > 1) {
      return t("sensors_generatexls_modal.error.date_range_exceeded");
    }
    if (isAfter(startDateParsed, endDateParsed)) {
      return t("sensors_generatexls_modal.error.start_after_end");
    }
    return null;
  };

  const handleGenerateReport = async (data: FormValues) => {
    const error = validateDateRange(data.startDate, data.endDate);
    if (error) {
      toast.error(error);
      return;
    }

    const filter = {
      start_date: format(
        parse(data.startDate, "dd/MM/yyyy HH:mm", new Date()),
        "yyyy-MM-dd HH:mm:ss"
      ),
      end_date: format(
        parse(data.endDate, "dd/MM/yyyy HH:mm", new Date()),
        "yyyy-MM-dd HH:mm:ss"
      ),

      alert_type_id: data.alertTypeId?.value || undefined,
      vehicle_plate: data.vehiclePlate?.label || undefined,
    };

    try {
      await ReportsService.generateSensorsExcelReport({ filter });

      dispatch(
        updateAlert({
          title: t("sensors.toast.report_available.prefix"),
          message: t("sensors.toast.report_available.link_text"),
          type: "success",
          onClick: () => {
            navigate("/reports");
          },
        })
      );
    } catch (error) {
      console.error(error);

      dispatch(
        updateAlert({
          title: t("sensors.toast.error_occurred"),
          type: "error",
        })
      );
    }
  };

  return (
    <Modal show={visible} size="lg" centered onHide={onClose}>
      <Modal.Header className="border-0 gap-2" closeButton>
        <FaFilter size={18} />
        <Modal.Title>{t("sensors_generatexls_modal.filter")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <form onSubmit={handleSubmit(handleGenerateReport)}>
          <Row>
            <Col xs={12} md={6}>
              <Controller
                name="startDate"
                control={control}
                rules={{
                  required: t("sensors_generatexls_modal.error.required"),
                }}
                render={({ field }) => (
                  <DateTimerPicker
                    onSelectDate={() => {}}
                    label={t("sensors_generatexls_modal.start_date")}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />
              {errors.startDate && (
                <p className="text-danger mt-1">{errors.startDate.message}</p>
              )}
            </Col>
            <Col xs={12} md={6}>
              <Controller
                name="endDate"
                control={control}
                rules={{
                  required: t("sensors_generatexls_modal.error.required"),
                }}
                render={({ field }) => (
                  <DateTimerPicker
                    onSelectDate={() => {}}
                    label={t("sensors_generatexls_modal.end_date")}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />
              {errors.endDate && (
                <p className="text-danger mt-1">{errors.endDate.message}</p>
              )}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              {t("sensors_generatexls_modal.vehicle")}
              <Controller
                name="vehiclePlate"
                control={control}
                render={({ field }) => (
                  <AsyncPaginate
                    isClearable
                    placeholder={t("input.select_text")}
                    loadOptions={(inputValue, options, additional) => {
                      return VehicleService.loadVehicleOptions(
                        inputValue,
                        options,
                        additional
                      );
                    }}
                    value={field.value}
                    onChange={(selected) => field.onChange(selected)}
                    additional={{ page: 1 }}
                  />
                )}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              {t("sensors_generatexls_modal.sensor")}
              <Controller
                name="alertTypeId"
                control={control}
                render={({ field }) => (
                  <AsyncPaginate
                    isClearable
                    placeholder={t("input.select_text")}
                    loadOptions={(inputValue, options, additional) => {
                      return SensorsService.loadSensorsOptions(
                        inputValue,
                        options,
                        additional
                      );
                    }}
                    value={field.value}
                    onChange={(selected) => field.onChange(selected)}
                    additional={{ page: 1 }}
                  />
                )}
              />
            </Col>
          </Row>

          <Stack
            direction="horizontal"
            className="mt-4 justify-content-center"
            gap={3}
          >
            <Button
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" />{" "}
                  {t("sensors_generatexls_modal.loading")}
                </>
              ) : (
                t("sensors_generatexls_modal.generate_excel")
              )}
            </Button>
          </Stack>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default SensorsReportsModal;
